import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import NotificationBell from '../NotificationBell';
import { 
  LayoutDashboard, 
  Building,
  Users,
  FileText,
  Settings, 
  LogOut,
  Menu,
  X,
  ChevronRight,
  Shield,
  Mail,
  Layers,
  GitBranch,
  UserCog,
  UserPlus,
  AlertTriangle,
  Truck,
  Package,
  CreditCard
} from 'lucide-react';
import { Button } from '../ui/button';

const SPOOKER_LOGO = "https://spooker.io/wp-content/uploads/2022/12/logo.svg";

const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navigation = [
    { name: 'Dashboard', href: '/admin', icon: LayoutDashboard },
    { name: 'Aanmeldingen', href: '/admin/aanmeldingen', icon: UserPlus },
    { name: 'Organisaties', href: '/admin/organizations', icon: Building },
    { name: 'Leveranciers', href: '/admin/leveranciers', icon: Truck },
    { name: 'Marketplace', href: '/admin/marketplace', icon: Users },
    { name: 'Gebruikers', href: '/admin/users', icon: UserCog },
    { name: 'Modules', href: '/admin/modules', icon: Package },
    { name: 'Abonnement Tiers', href: '/admin/tiers', icon: CreditCard },
    { name: 'Betalingen', href: '/admin/payment-config', icon: CreditCard },
    { name: 'Offertes', href: '/admin/quotes', icon: FileText },
    { name: 'Dreigingsbeeld', href: '/admin/threats', icon: AlertTriangle },
    { name: 'Email', href: '/admin/email', icon: Mail },
    { name: 'Email Templates', href: '/admin/email-templates', icon: FileText },
    { name: 'Frameworks', href: '/admin/frameworks', icon: Layers },
    { name: 'Mappings', href: '/admin/mappings', icon: GitBranch },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (href) => {
    if (href === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black/20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-black transform transition-transform duration-200 ease-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-neutral-800">
            <Link to="/admin" className="flex items-center gap-2 text-white">
              <img src={SPOOKER_LOGO} alt="Spooker" className="h-5 brightness-0 invert" />
              <span className="text-sm font-bold tracking-tight">Admin</span>
            </Link>
            <button
              className="lg:hidden p-1 hover:bg-neutral-800 rounded text-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="h-5 w-5" strokeWidth={1.5} />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.href);
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors duration-150 ${
                    active
                      ? 'bg-neutral-800 text-white'
                      : 'text-neutral-400 hover:bg-neutral-800 hover:text-white'
                  }`}
                  onClick={() => setSidebarOpen(false)}
                  data-testid={`admin-nav-${item.name.toLowerCase()}`}
                >
                  <Icon className="h-5 w-5" strokeWidth={1.5} />
                  {item.name}
                  {active && <ChevronRight className="ml-auto h-4 w-4 text-neutral-500" strokeWidth={1.5} />}
                </Link>
              );
            })}
          </nav>

          {/* User Section */}
          <div className="border-t border-neutral-800 p-4">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-9 w-9 rounded-full bg-neutral-800 flex items-center justify-center">
                <span className="text-sm font-medium text-white">
                  {user?.full_name?.charAt(0)?.toUpperCase() || 'A'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
                <p className="text-xs text-neutral-500 truncate">Administrator</p>
              </div>
            </div>
            <Button
              variant="ghost"
              className="w-full justify-start text-neutral-400 hover:text-white hover:bg-neutral-800"
              onClick={handleLogout}
              data-testid="admin-logout-button"
            >
              <LogOut className="h-4 w-4 mr-2" strokeWidth={1.5} />
              Uitloggen
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Mobile Header */}
        <header className="sticky top-0 z-30 flex items-center justify-between h-16 px-4 bg-white border-b border-neutral-200 lg:hidden">
          <div className="flex items-center">
            <button
              className="p-2 -ml-2 hover:bg-neutral-100 rounded-md"
              onClick={() => setSidebarOpen(true)}
              data-testid="admin-mobile-menu-button"
            >
              <Menu className="h-5 w-5" strokeWidth={1.5} />
            </button>
            <img src={SPOOKER_LOGO} alt="Spooker" className="h-5 ml-3" />
          </div>
          <NotificationBell />
        </header>

        {/* Desktop Header */}
        <header className="hidden lg:flex sticky top-0 z-30 items-center justify-end h-16 px-6 bg-white border-b border-neutral-200">
          <NotificationBell />
        </header>

        {/* Page Content */}
        <main className="min-h-[calc(100vh-8rem)] lg:min-h-[calc(100vh-4rem)]">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
